import posthog from 'posthog-js';
import { RefObject, useEffect, useRef } from 'react';
import ApiContentManager from 'services/api/ApiContentManager';
import useIsAppVisible from './useIsAppVisible';

// Reaching this depth counts as having read the post.
const COMPLETED_DEPTH = 0.9;
// Scroll depths at which progress is reported, so that a read is measured even
// if the final report (when the post is closed) never leaves the device (app
// killed, network gone). The completion depth is one of them, so a completed
// read is never missed for want of that final report.
const MILESTONES = [0.25, 0.5, 0.75, COMPLETED_DEPTH, 1];

type Reason = 'milestone' | 'background' | 'closed';

/**
 * Measures how much of a post the user actually read: the deepest point
 * scrolled to in `scrollRef` (1 = reached the end) and the time the post was
 * visible on screen. Reports it to the backend and to PostHog at scroll
 * milestones, when the app goes to the background and when the post is closed.
 */
export default function useReadProgress(
  scrollRef: RefObject<HTMLDivElement | null>,
  postId: string | undefined,
  loaded: boolean,
) {
  const isAppVisible = useIsAppVisible();
  const maxDepth = useRef(0);
  const visibleSince = useRef<number | null>(null);
  const accumulatedMs = useRef(0);
  const nextMilestone = useRef(0);
  // set by the measuring effect below, so the visibility effect can report too
  const report = useRef<(reason: Reason) => void>(() => undefined);

  useEffect(() => {
    const container = scrollRef.current;
    if (!postId || !loaded || !container) return undefined;

    maxDepth.current = 0;
    accumulatedMs.current = 0;
    nextMilestone.current = 0;
    visibleSince.current = Date.now();

    const seconds = () => {
      const open = visibleSince.current ? Date.now() - visibleSince.current : 0;
      return Math.round((accumulatedMs.current + open) / 1000);
    };

    report.current = (reason: Reason) => {
      const progress = { scroll_depth: Math.round(maxDepth.current * 100) / 100, seconds: seconds() };
      ApiContentManager.sendReadProgress(postId, progress).catch(() => undefined);
      posthog.capture('info.post_read_progress', {
        post_id: postId,
        scroll_depth: progress.scroll_depth,
        read_seconds: progress.seconds,
        completed: progress.scroll_depth >= COMPLETED_DEPTH,
        reason,
      });
    };

    const measure = () => {
      const { scrollTop, clientHeight, scrollHeight } = container;
      // nothing laid out (yet, or any more once the page is left): no reading
      if (scrollHeight === 0 || clientHeight === 0) return;
      // a post short enough to fit on screen is fully visible right away
      const depth = scrollHeight <= clientHeight ? 1 : Math.min(1, (scrollTop + clientHeight) / scrollHeight);
      if (depth > maxDepth.current) {
        maxDepth.current = depth;
      }
      if (nextMilestone.current < MILESTONES.length && maxDepth.current >= MILESTONES[nextMilestone.current]) {
        while (nextMilestone.current < MILESTONES.length && maxDepth.current >= MILESTONES[nextMilestone.current]) {
          nextMilestone.current += 1;
        }
        report.current('milestone');
      }
    };

    // the content (images, embeds) may still be laying out: measure once now
    // and again shortly after so short posts register as fully visible
    measure();
    const initialMeasure = setTimeout(measure, 500);
    container.addEventListener('scroll', measure, { passive: true });

    return () => {
      clearTimeout(initialMeasure);
      container.removeEventListener('scroll', measure);
      // the container is already detached here: report the last measurement
      report.current('closed');
      report.current = () => undefined;
    };
  }, [scrollRef, postId, loaded]);

  useEffect(() => {
    if (isAppVisible) {
      visibleSince.current = Date.now();
      return;
    }
    if (visibleSince.current) {
      accumulatedMs.current += Date.now() - visibleSince.current;
      visibleSince.current = null;
    }
    // the app may not come back: report what we have
    report.current('background');
  }, [isAppVisible]);
}
